import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen } from "lucide-react";
import { toast } from "sonner";
// Drag-only picker rows. Click does nothing — see plan 2026-05-04.
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { LibrarySearchPicker } from "@/components/library/LibrarySearchPicker";
import { SourceTypeFilterPopover } from "@/components/excerpts/filterBar/SourceTypeFilterPopover";
import { supabase } from "@/integrations/supabase/client";
import { getSourceRowToneVars } from "@/lib/sourceTypeRowTone";
import { getSourceTypeIcon } from "@/lib/sourceTypeIcons";
import { getLibrarySourceTypeLabel, type LibrarySourceType } from "@/lib/library";
import { parseMediaFragmentSeconds } from "@/lib/composer/excerptDropFormat";

export const COMPOSER_EXCERPT_DRAG_MIME = "application/x-excerptus-excerpt-ref";

type ComposerExcerptRow = {
  id: string;
  body_text: string | null;
  body_html: string | null;
  created_at: string;
  page_start: number | null;
  page_end: number | null;
  source_url: string | null;
  library_items: {
    id: string;
    title: string | null;
    display_title: string | null;
    author_text: string | null;
    published_year: number | null;
    published_date: string | null;
    source_type: LibrarySourceType | null;
    container_title: string | null;
    accessed_date: string | null;
    isbn_10: string | null;
    isbn_13: string | null;
  } | null;
};

const PAGE_SIZE = 50;

const stripHtml = (s: string | null | undefined) =>
  (s ?? "").replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();

type RowProps = {
  row: ComposerExcerptRow;
};

const ExcerptRow = ({ row }: RowProps) => {
  const sourceType = (row.library_items?.source_type ?? "other") as LibrarySourceType;
  const tone = getSourceRowToneVars(sourceType);
  const SourceIcon = getSourceTypeIcon(sourceType);
  const [hovered, setHovered] = useState(false);
  const author = row.library_items?.author_text ?? null;
  const pub = row.library_items?.display_title ?? row.library_items?.title ?? null;
  const publishedDate = row.library_items?.published_date ?? null;
  const yearFromDate = publishedDate?.match(/\b(\d{4})\b/)?.[1] ?? null;
  const year = row.library_items?.published_year ?? (yearFromDate ? Number.parseInt(yearFromDate, 10) : null);
  // For podcasts, prefer the full published_date (e.g. "2025-04-12") in the dropped citation;
  // otherwise fall back to the bare year. The list card still shows just the year.
  const dropYear: string | number | null =
    row.library_items?.source_type === "podcast" && publishedDate ? publishedDate : year;
  const pageRange = row.page_start
    ? row.page_end && row.page_end !== row.page_start
      ? `pp. ${row.page_start}–${row.page_end}`
      : `p. ${row.page_start}`
    : null;
  const snippet = stripHtml(row.body_text).slice(0, 220);

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    const timestampSeconds = parseMediaFragmentSeconds(row.source_url);
    const payload = JSON.stringify({
      kind: "excerpt",
      id: row.id,
      library_item_id: row.library_items?.id ?? null,
      body_text: row.body_text ?? "",
      body_html: row.body_html ?? "",
      author: author ?? "",
      publication: pub ?? "",
      year: dropYear,
      page_start: row.page_start ?? null,
      page_end: row.page_end ?? null,
      source_type: row.library_items?.source_type ?? null,
      container_title: row.library_items?.container_title ?? null,
      accessed_date: row.library_items?.accessed_date ?? null,
      isbn_10: row.library_items?.isbn_10 ?? null,
      isbn_13: row.library_items?.isbn_13 ?? null,
      timestamp_seconds: timestampSeconds,
    });
    e.dataTransfer.setData(COMPOSER_EXCERPT_DRAG_MIME, payload);
    e.dataTransfer.setData("text/plain", row.body_text ?? "");
    e.dataTransfer.effectAllowed = "copy";
  };

  return (
    <div
      role="listitem"
      draggable
      onDragStart={handleDragStart}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        backgroundColor: hovered ? tone.hover : tone.idle,
        borderLeft: `4px solid ${tone.accent}`,
      }}
      className="flex w-full items-start gap-3 rounded-md p-2 text-left select-none cursor-grab active:cursor-grabbing transition-colors"
    >
      <div
        draggable={false}
        className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md pointer-events-none"
        style={{ backgroundColor: tone.selected, color: tone.title }}
        aria-hidden="true"
      >
        <SourceIcon className="h-4 w-4" />
      </div>
      <div draggable={false} className="min-w-0 flex-1 pointer-events-none">
        <div className="line-clamp-2 text-sm" style={{ color: tone.title }}>
          {snippet || <span className="italic opacity-70">(empty excerpt)</span>}
        </div>
        <div className="mt-1 truncate text-xs" style={{ color: tone.meta }}>
          {[author, pub, year ? String(year) : null, pageRange].filter(Boolean).join(" · ") || "Unknown source"}
          {" "}
          <span
            className="ml-1 inline-flex items-center rounded-full px-1.5 py-0 align-middle text-[10px] font-semibold"
            style={{ color: tone.title, borderColor: tone.selected, borderWidth: 1, backgroundColor: "transparent" }}
          >
            {getLibrarySourceTypeLabel(sourceType)}
          </span>
        </div>
      </div>
    </div>
  );
};

export type ComposerExcerptsPickerProps = {
  workspaceId: string | undefined;
};

const SELECT_COLS =
  "id, body_text, body_html, created_at, page_start, page_end, source_url, library_items(id, title, display_title, author_text, published_year, published_date, source_type, container_title, accessed_date, isbn_10, isbn_13)";

export const ComposerExcerptsPicker = ({ workspaceId }: ComposerExcerptsPickerProps) => {
  const [rows, setRows] = useState<ComposerExcerptRow[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selectedTypes, setSelectedTypes] = useState<NonNullable<LibrarySourceType>[]>([]);
  const reqIdRef = useRef(0);

  // Debounce search input.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  // Total count (matches /excerpts chip — workspace-scoped, ignores search).
  useEffect(() => {
    let cancelled = false;
    if (!workspaceId) {
      setTotalCount(null);
      return;
    }
    supabase
      .from("excerpts")
      .select("id", { count: "exact", head: true })
      .eq("workspace_id", workspaceId)
      .then(({ count, error }) => {
        if (cancelled) return;
        setTotalCount(error ? null : count ?? 0);
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  const runQuery = useCallback(
    async (cursor: string | null) => {
      if (!workspaceId) return [] as ComposerExcerptRow[];
      const hasTypeFilter = selectedTypes.length > 0;
      const selectCols = hasTypeFilter
        ? SELECT_COLS.replace("library_items(", "library_items!inner(")
        : SELECT_COLS;
      let q = supabase
        .from("excerpts")
        .select(selectCols)
        .eq("workspace_id", workspaceId)
        .order("created_at", { ascending: false })
        .limit(PAGE_SIZE);
      if (cursor) q = q.lt("created_at", cursor);
      if (debouncedQuery) {
        q = q.textSearch("search_tsv", debouncedQuery, { type: "websearch", config: "simple" });
      }
      if (hasTypeFilter) {
        q = q.in("library_items.source_type", selectedTypes);
      }
      const { data, error } = await q;
      if (error) {
        toast.error(error.message);
        return [];
      }
      return (data as unknown as ComposerExcerptRow[]) ?? [];
    },
    [workspaceId, debouncedQuery, selectedTypes],
  );

  // Initial / search-changed / filter-changed load.
  useEffect(() => {
    if (!workspaceId) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const reqId = ++reqIdRef.current;
    runQuery(null).then((data) => {
      if (reqId !== reqIdRef.current) return;
      setRows(data);
      setHasMore(data.length === PAGE_SIZE);
      setLoading(false);
    });
  }, [workspaceId, debouncedQuery, selectedTypes, runQuery]);

  const handleLoadMore = async () => {
    if (loadingMore || !hasMore || rows.length === 0) return;
    setLoadingMore(true);
    const cursor = rows[rows.length - 1]?.created_at ?? null;
    const data = await runQuery(cursor);
    setRows((prev) => [...prev, ...data]);
    setHasMore(data.length === PAGE_SIZE);
    setLoadingMore(false);
  };

  return (
    <div className="flex h-full flex-col gap-3">
      <div className="flex items-center gap-2">
        <BookOpen className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">My Excerpts</span>
        <Badge variant="outline" className="ml-auto text-[10px] uppercase tracking-wider">
          {totalCount ?? "—"}
        </Badge>
      </div>

      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <LibrarySearchPicker value={query} onChange={setQuery} />
        </div>
        <SourceTypeFilterPopover
          selected={selectedTypes}
          onToggle={(value) =>
            setSelectedTypes((prev) =>
              prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
            )
          }
          onClear={() => setSelectedTypes([])}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : !workspaceId ? (
          <p className="py-6 text-center text-xs text-muted-foreground">
            Pick a workspace to browse its excerpts.
          </p>
        ) : rows.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center">
            <p className="text-sm text-muted-foreground">
              {debouncedQuery || selectedTypes.length > 0
                ? "No excerpts match your filters."
                : "No excerpts yet."}
            </p>
            {!debouncedQuery && (
              <Button asChild size="sm" variant="outline">
                <Link to="/excerpts">Open My Excerpts</Link>
              </Button>
            )}
          </div>
        ) : (
          <>
            <ul className="divide-y divide-border/60">
              {rows.map((row) => (
                <li key={row.id}>
                  <ExcerptRow row={row} />
                </li>
              ))}
            </ul>
            {hasMore && (
              <div className="flex justify-center pt-2">
                <Button size="sm" variant="outline" onClick={handleLoadMore} disabled={loadingMore}>
                  {loadingMore ? "Loading…" : "Load more"}
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <div className="border-t border-border/60 pt-2 text-right">
        <Button asChild size="sm" variant="ghost" className="h-7 text-xs">
          <Link to="/excerpts">Manage My Excerpts →</Link>
        </Button>
      </div>
    </div>
  );
};
